import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  Box,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Chip,
  Divider,
} from "@mui/material";
import { X, MapPin, Users, Video, Clock, Trash2 } from "lucide-react";
import { useOutlookCalendar } from "./hooks";
import axios from "axios";
import momentTz from "moment-timezone";
import * as constants from "../../utils/constants";

const localizer = momentLocalizer(moment);

const API_URL = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}`;

// Microsoft timezone to IANA mapping (for multi-connection raw event transform)
const MS_TO_IANA_TZ = {
  UTC: "UTC",
  "Etc/UTC": "UTC",
  "India Standard Time": "Asia/Kolkata",
  "Pacific Standard Time": "America/Los_Angeles",
  "Pacific Daylight Time": "America/Los_Angeles",
  "Eastern Standard Time": "America/New_York",
  "Eastern Daylight Time": "America/New_York",
  "Central Standard Time": "America/Chicago",
  "Central Daylight Time": "America/Chicago",
  "Mountain Standard Time": "America/Denver",
  "Mountain Daylight Time": "America/Denver",
  "GMT Standard Time": "Europe/London",
  "British Summer Time": "Europe/London",
  "Central European Standard Time": "Europe/Berlin",
  "W. Europe Standard Time": "Europe/Berlin",
  "Romance Standard Time": "Europe/Paris",
  "Singapore Standard Time": "Asia/Singapore",
  "Tokyo Standard Time": "Asia/Tokyo",
  "China Standard Time": "Asia/Shanghai",
  "AUS Eastern Standard Time": "Australia/Sydney",
  "Arabian Standard Time": "Asia/Dubai",
};

const parseGraphDateTime = (dateTimeObj) => {
  if (!dateTimeObj) return null;
  if (typeof dateTimeObj === "string") return dateTimeObj;
  const dateTime =
    dateTimeObj.dateTime?.replace(/\.\d+$/, "") || dateTimeObj.dateTime;
  const msTimeZone = dateTimeObj.timeZone || "UTC";
  const ianaTimeZone = MS_TO_IANA_TZ[msTimeZone] || msTimeZone;
  try {
    return momentTz.tz(dateTime, ianaTimeZone).toDate().toISOString();
  } catch (e) {
    return dateTime + "Z";
  }
};

const transformRawEvent = (event) => ({
  id: event.id,
  title: event.subject,
  start: parseGraphDateTime(event.start),
  end: parseGraphDateTime(event.end),
  allDay: event.isAllDay || false,
  extendedProps: {
    location: event.location?.displayName || "",
    description: event.bodyPreview || event.body?.content || "",
    organizer: event.organizer?.emailAddress?.address || "",
    attendees: event.attendees || [],
    webLink: event.webLink,
    onlineMeetingUrl: event.onlineMeetingUrl,
    isOnlineMeeting: event.isOnlineMeeting,
    importance: event.importance,
    showAs: event.showAs,
    responseStatus: event.responseStatus?.response,
    timeZone: event.start?.timeZone || "UTC",
  },
});

// Color palette for multiple accounts
const ACCOUNT_COLORS = [
  { bg: "#1976d2", text: "#fff", light: "#e3f2fd" },
  { bg: "#388e3c", text: "#fff", light: "#e8f5e9" },
  { bg: "#f57c00", text: "#fff", light: "#fff3e0" },
  { bg: "#7b1fa2", text: "#fff", light: "#f3e5f5" },
  { bg: "#c62828", text: "#fff", light: "#ffebee" },
  { bg: "#00838f", text: "#fff", light: "#e0f7fa" },
];

/**
 * OutlookCalendar - Full calendar component for Outlook integration
 * Supports single connection (connectionId) or multiple connections (connections array)
 * Also supports passing events directly (for common free slots view)
 */
const OutlookCalendar = ({
  connectionId = null,
  connections = null,
  height = "100%",
  targetUserId = null,
  events: externalEvents = null,
}) => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewEventOpen, setViewEventOpen] = useState(false);
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: "",
    start: "",
    end: "",
    location: "",
    description: "",
    allDay: false,
    isOnlineMeeting: false,
  });
  const [attendeesList, setAttendeesList] = useState([]);
  const [attendeeInput, setAttendeeInput] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState(Views.MONTH);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Use external events when provided (common free slots mode)
  useEffect(() => {
    if (externalEvents && externalEvents.length > 0) {
      setEvents(externalEvents);
    }
  }, [externalEvents]);

  // Use the hook only for single-connection mode (for create/update/delete)
  const activeConnectionId =
    connectionId ||
    (connections?.length === 1 ? connections[0].id : connections?.[0]?.id);
  const {
    loading,
    error,
    getEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    getEvent,
  } = useOutlookCalendar(activeConnectionId, { targetUserId });

  // Build a color map for connections
  const colorMapRef = useRef({});
  if (connections && connections.length > 0) {
    connections.forEach((conn, idx) => {
      if (!colorMapRef.current[conn.id]) {
        colorMapRef.current[conn.id] =
          ACCOUNT_COLORS[idx % ACCOUNT_COLORS.length];
      }
    });
  }

  // Transform raw events for react-big-calendar
  const transformEvents = useCallback(
    (fetchedEvents, connId = null, connEmail = null) => {
      return fetchedEvents
        .map((event) => {
          let startStr = event.start;
          let endStr = event.end;
          if (typeof startStr === "string")
            startStr = startStr.replace(/\.\d+$/, "");
          if (typeof endStr === "string") endStr = endStr.replace(/\.\d+$/, "");
          const startDate = new Date(startStr);
          const endDate = new Date(endStr);
          return {
            ...event,
            start: startDate,
            end: endDate,
            _connectionId: connId,
            _connectionEmail: connEmail,
          };
        })
        .filter(
          (event) =>
            !isNaN(event.start.getTime()) && !isNaN(event.end.getTime()),
        );
    },
    [],
  );

  // Fetch events from a single connection via direct API call (with Graph API transform)
  const fetchEventsForConnection = useCallback(
    async (connId, startISO, endISO) => {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}/api/v1/outlook/events/`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
        params: {
          start_date: startISO,
          end_date: endISO,
          connection_id: connId,
        },
      });
      const rawEvents = response.data.events || [];
      return rawEvents.map(transformRawEvent);
    },
    [],
  );

  // Fetch events - supports single, multi-connection, and admin (targetUserId) modes
  const fetchEvents = useCallback(async () => {
    const isAdminMode = !!targetUserId;
    const isMulti = connections && connections.length > 0;
    const isSingle = !isMulti && connectionId;

    if (!isAdminMode && !isMulti && !isSingle) {
      console.log("No connections, skipping fetch");
      return;
    }

    const start = moment(currentDate).startOf("month").subtract(7, "days");
    const end = moment(currentDate).endOf("month").add(7, "days");
    const startISO = start.toISOString();
    const endISO = end.toISOString();

    setFetchLoading(true);
    setFetchError(null);

    try {
      if (isAdminMode) {
        // Admin mode - use the hook with targetUserId
        const fetchedEvents = await getEvents(startISO, endISO);
        const transformed = transformEvents(fetchedEvents);
        setEvents(transformed);
      } else if (isMulti) {
        // Fetch from all connections in parallel
        const results = await Promise.allSettled(
          connections.map((conn) =>
            fetchEventsForConnection(conn.id, startISO, endISO).then(
              (rawEvents) => transformEvents(rawEvents, conn.id, conn.email),
            ),
          ),
        );
        const allEvents = results
          .filter((r) => r.status === "fulfilled")
          .flatMap((r) => r.value);
        console.log(
          `Fetched ${allEvents.length} events from ${connections.length} accounts`,
        );
        setEvents(allEvents);
      } else {
        // Single connection mode - use the hook
        const fetchedEvents = await getEvents(startISO, endISO);
        const transformed = transformEvents(fetchedEvents, connectionId);
        console.log("Transformed events:", transformed.length, "events");
        setEvents(transformed);
      }
    } catch (err) {
      console.error("Failed to fetch events:", err);
      setFetchError("Failed to fetch calendar events");
    } finally {
      setFetchLoading(false);
    }
  }, [
    currentDate,
    connectionId,
    connections,
    targetUserId,
    getEvents,
    fetchEventsForConnection,
    transformEvents,
  ]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Handle event click
  const handleSelectEvent = useCallback(
    async (event) => {
      if (targetUserId && event.id) {
        try {
          const fullEvent = await getEvent(event.id);
          const startDate = new Date(fullEvent.start);
          const endDate = new Date(fullEvent.end);
          setSelectedEvent({ ...fullEvent, start: startDate, end: endDate });
        } catch (err) {
          console.error("Failed to fetch event details:", err);
          setSelectedEvent(event);
        }
      } else {
        setSelectedEvent(event);
      }
      setViewEventOpen(true);
    },
    [targetUserId, getEvent],
  );

  // Format date to local datetime-local input format (YYYY-MM-DDTHH:MM)
  const formatLocalDateTime = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Handle slot select (create new event)
  const handleSelectSlot = useCallback((slotInfo) => {
    setEventForm({
      title: "",
      start: formatLocalDateTime(slotInfo.start),
      end: formatLocalDateTime(slotInfo.end),
      location: "",
      description: "",
      allDay: slotInfo.action === "doubleClick" && slotInfo.slots.length === 1,
      isOnlineMeeting: false,
    });
    setAttendeesList([]);
    setCreateEventOpen(true);
  }, []);

  // Handle event drop (drag and drop)
  const handleEventDrop = useCallback(
    async ({ event, start, end }) => {
      try {
        // Format as local datetime string (not UTC) to match user's timezone
        const formatLocalDateTime = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          return `${year}-${month}-${day}T${hours}:${minutes}:00`;
        };

        await updateEvent(event.id, {
          start: formatLocalDateTime(start),
          end: formatLocalDateTime(end),
        });
        fetchEvents();
      } catch (err) {
        console.error("Failed to update event:", err);
      }
    },
    [updateEvent, fetchEvents],
  );

  // Add attendee to list
  const handleAddAttendee = useCallback(() => {
    const email = attendeeInput.trim();
    if (email && email.includes("@") && !attendeesList.includes(email)) {
      setAttendeesList((prev) => [...prev, email]);
      setAttendeeInput("");
    }
  }, [attendeeInput, attendeesList]);

  // Remove attendee from list
  const handleRemoveAttendee = useCallback((email) => {
    setAttendeesList((prev) => prev.filter((e) => e !== email));
  }, []);

  // Handle attendee input keypress (Enter to add)
  const handleAttendeeKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        handleAddAttendee();
      }
    },
    [handleAddAttendee],
  );

  // Create new event
  const handleCreateEvent = useCallback(async () => {
    setCreating(true);
    try {
      console.log("Creating event with form data:", eventForm);

      const newEvent = await createEvent({
        ...eventForm,
        attendees: attendeesList,
      });

      console.log("Created event response:", newEvent);

      // Optimistic update - add to local state immediately
      if (newEvent) {
        const startDate = new Date(newEvent.start);
        const endDate = new Date(newEvent.end);

        console.log("Parsed dates:", {
          startStr: newEvent.start,
          startDate,
          endStr: newEvent.end,
          endDate,
          startValid: !isNaN(startDate.getTime()),
          endValid: !isNaN(endDate.getTime()),
        });

        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          setEvents((prev) => [
            ...prev,
            {
              ...newEvent,
              start: startDate,
              end: endDate,
            },
          ]);
        }
      }

      setCreateEventOpen(false);
      setEventForm({
        title: "",
        start: "",
        end: "",
        location: "",
        description: "",
        allDay: false,
        isOnlineMeeting: false,
      });
      setAttendeesList([]);

      // Immediate refresh plus delayed refresh to ensure sync with server
      await fetchEvents();
      setTimeout(() => fetchEvents(), 3000);
    } catch (err) {
      console.error("Failed to create event:", err);
    } finally {
      setCreating(false);
    }
  }, [eventForm, attendeesList, createEvent, fetchEvents]);

  // Delete event
  const handleDeleteEvent = useCallback(async () => {
    if (!selectedEvent) return;

    setDeleting(true);
    try {
      await deleteEvent(selectedEvent.id);

      // Optimistic update - remove from local state immediately
      setEvents((prev) => prev.filter((e) => e.id !== selectedEvent.id));

      setViewEventOpen(false);
      setSelectedEvent(null);

      // Background refresh to sync with server
      setTimeout(() => fetchEvents(), 1000);
    } catch (err) {
      console.error("Failed to delete event:", err);
    } finally {
      setDeleting(false);
    }
  }, [selectedEvent, deleteEvent, fetchEvents]);

  // Format date for display
  const formatDateTime = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // Color-code events by account in multi-connection mode and common free slots
  const eventPropGetter = useCallback(
    (event) => {
      // Common free slots styling
      if (event.isCommonSlot) {
        return {
          style: {
            backgroundColor: "#4caf50",
            color: "#fff",
            borderRadius: "4px",
            border: "none",
            opacity: 0.9,
          },
        };
      }
      // Multi-connection color coding
      if (connections && connections.length > 1 && event._connectionId) {
        const color = colorMapRef.current[event._connectionId];
        if (color) {
          return {
            style: {
              backgroundColor: color.bg,
              color: color.text,
              borderRadius: "4px",
              border: "none",
              opacity: 0.9,
            },
          };
        }
      }
      return {};
    },
    [connections],
  );

  const isLoading = loading || fetchLoading;
  const displayError = error || fetchError;

  return (
    <Box sx={{ height, display: "flex", flexDirection: "column" }}>
      {displayError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {displayError}
        </Alert>
      )}

      {/* Account color legend for multi-connection mode */}
      {connections && connections.length > 1 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: 1,
            px: 2,
            pt: 1,
          }}
        >
          {connections.map((conn, idx) => {
            const color = ACCOUNT_COLORS[idx % ACCOUNT_COLORS.length];
            return (
              <Chip
                key={conn.id}
                label={conn.email}
                size="small"
                sx={{
                  backgroundColor: color.bg,
                  color: color.text,
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              />
            );
          })}
        </Box>
      )}

      <Paper sx={{ flex: 1, p: 2, overflow: "hidden" }}>
        {isLoading && events.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            titleAccessor="title"
            style={{ height: "100%" }}
            views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
            view={currentView}
            date={currentDate}
            onNavigate={setCurrentDate}
            onView={setCurrentView}
            selectable
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            onEventDrop={handleEventDrop}
            eventPropGetter={eventPropGetter}
            popup
            showMultiDayTimes
            step={30}
            timeslots={1}
          />
        )}
      </Paper>

      {/* View Event Dialog */}
      <Dialog
        open={viewEventOpen}
        onClose={() => setViewEventOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {selectedEvent?.title}
          </Typography>
          <IconButton onClick={() => setViewEventOpen(false)} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEvent && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Join Meeting Button - Prominent at top */}
              {(() => {
                // Extract Teams meeting URL from description or use onlineMeetingUrl
                let meetingUrl = selectedEvent.extendedProps?.onlineMeetingUrl;
                if (!meetingUrl && selectedEvent.extendedProps?.description) {
                  // Try to extract Teams URL from description
                  const teamsMatch =
                    selectedEvent.extendedProps.description.match(
                      /https:\/\/teams\.microsoft\.com\/[^\s"<]+/,
                    );
                  if (teamsMatch) meetingUrl = teamsMatch[0];
                }

                return meetingUrl ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<Video size={20} />}
                    href={meetingUrl}
                    target="_blank"
                    sx={{ py: 1.5 }}
                  >
                    Join Teams Meeting
                  </Button>
                ) : selectedEvent.extendedProps?.isOnlineMeeting ? (
                  <Chip
                    label="Online Meeting"
                    color="primary"
                    icon={<Video size={16} />}
                  />
                ) : null;
              })()}

              {/* Date & Time */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Clock size={18} color="#666" />
                <Typography>
                  {formatDateTime(selectedEvent.start)}
                  {selectedEvent.end &&
                    ` - ${formatDateTime(selectedEvent.end)}`}
                </Typography>
                {selectedEvent.allDay && (
                  <Chip label="All Day" size="small" color="info" />
                )}
              </Box>

              {/* Location */}
              {(selectedEvent.extendedProps?.location ||
                selectedEvent.location) && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <MapPin size={18} color="#666" style={{ marginTop: 2 }} />
                  <Typography>
                    {selectedEvent.extendedProps?.location ||
                      selectedEvent.location}
                  </Typography>
                </Box>
              )}

              {/* Account (multi-connection mode) */}
              {selectedEvent._connectionEmail &&
                connections &&
                connections.length > 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label={selectedEvent._connectionEmail}
                      size="small"
                      sx={{
                        backgroundColor:
                          colorMapRef.current[selectedEvent._connectionId]
                            ?.bg || "#666",
                        color: "#fff",
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                )}

              {/* Organizer */}
              {selectedEvent.extendedProps?.organizer && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Users size={18} color="#666" />
                  <Typography>
                    <strong>Organizer:</strong>{" "}
                    {selectedEvent.extendedProps.organizer}
                  </Typography>
                </Box>
              )}

              {/* Attendees */}
              {selectedEvent.extendedProps?.attendees?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Attendees ({selectedEvent.extendedProps.attendees.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selectedEvent.extendedProps.attendees.map(
                      (attendee, index) => (
                        <Chip
                          key={index}
                          label={
                            attendee.emailAddress?.name ||
                            attendee.emailAddress?.address
                          }
                          size="small"
                          variant="outlined"
                          color={
                            attendee.status?.response === "accepted"
                              ? "success"
                              : attendee.status?.response === "declined"
                                ? "error"
                                : "default"
                          }
                        />
                      ),
                    )}
                  </Box>
                </Box>
              )}

              {/* Description */}
              {selectedEvent.extendedProps?.description && (
                <Box>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Description
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: "grey.50",
                      p: 1.5,
                      borderRadius: 1,
                      maxHeight: 200,
                      overflow: "auto",
                      "& a": { color: "primary.main", wordBreak: "break-all" },
                      "& img": { maxWidth: "100%", height: "auto" },
                      "& p": { margin: "4px 0" },
                      "& div": { margin: "4px 0" },
                      fontSize: "14px",
                      lineHeight: 1.5,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: selectedEvent.extendedProps.description
                        .replace(/\r\n/g, "<br/>")
                        .replace(/\n/g, "<br/>"),
                    }}
                  />
                </Box>
              )}

              {/* Meeting Status */}
              {selectedEvent.extendedProps?.showAs && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={`Status: ${selectedEvent.extendedProps.showAs}`}
                    size="small"
                    color={
                      selectedEvent.extendedProps.showAs === "busy"
                        ? "error"
                        : selectedEvent.extendedProps.showAs === "tentative"
                          ? "warning"
                          : "default"
                    }
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Button
            color="error"
            startIcon={
              deleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Trash2 size={16} />
              )
            }
            onClick={handleDeleteEvent}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            {selectedEvent?.extendedProps?.webLink && (
              <Button
                variant="outlined"
                href={selectedEvent.extendedProps.webLink}
                target="_blank"
                size="small"
              >
                Open in Outlook
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog
        open={createEventOpen}
        onClose={() => setCreateEventOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">New Event</Typography>
            <IconButton onClick={() => setCreateEventOpen(false)} size="small">
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Title"
              value={eventForm.title}
              onChange={(e) =>
                setEventForm({ ...eventForm, title: e.target.value })
              }
              fullWidth
              required
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Start"
                type="datetime-local"
                value={eventForm.start?.slice(0, 16) || ""}
                onChange={(e) =>
                  setEventForm({ ...eventForm, start: e.target.value })
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End"
                type="datetime-local"
                value={eventForm.end?.slice(0, 16) || ""}
                onChange={(e) =>
                  setEventForm({ ...eventForm, end: e.target.value })
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              label="Location"
              value={eventForm.location}
              onChange={(e) =>
                setEventForm({ ...eventForm, location: e.target.value })
              }
              fullWidth
              placeholder="Add a location"
            />

            <Box>
              <TextField
                label="Add Attendees"
                value={attendeeInput}
                onChange={(e) => setAttendeeInput(e.target.value)}
                onKeyDown={handleAttendeeKeyPress}
                fullWidth
                placeholder="Enter email and press Enter"
                helperText="Press Enter or comma to add"
                InputProps={{
                  endAdornment: (
                    <Button
                      size="small"
                      onClick={handleAddAttendee}
                      disabled={!attendeeInput.trim()}
                    >
                      Add
                    </Button>
                  ),
                }}
              />
              {attendeesList.length > 0 && (
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}
                >
                  {attendeesList.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveAttendee(email)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </Box>

            <TextField
              label="Description"
              value={eventForm.description}
              onChange={(e) =>
                setEventForm({ ...eventForm, description: e.target.value })
              }
              fullWidth
              multiline
              rows={3}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={eventForm.allDay}
                    onChange={(e) =>
                      setEventForm({ ...eventForm, allDay: e.target.checked })
                    }
                  />
                }
                label="All Day"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={eventForm.isOnlineMeeting}
                    onChange={(e) =>
                      setEventForm({
                        ...eventForm,
                        isOnlineMeeting: e.target.checked,
                      })
                    }
                  />
                }
                label="Online Meeting"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateEventOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateEvent}
            disabled={!eventForm.title || creating}
          >
            {creating ? <CircularProgress size={20} /> : "Create Event"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

OutlookCalendar.propTypes = {
  connectionId: PropTypes.string,
  connections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      email: PropTypes.string.isRequired,
    }),
  ),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  targetUserId: PropTypes.string,
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      start: PropTypes.instanceOf(Date).isRequired,
      end: PropTypes.instanceOf(Date).isRequired,
      isCommonSlot: PropTypes.bool,
    }),
  ),
};

export default OutlookCalendar;
